import { updateRole } from '@app/api/role.api';
import { QueryTable } from '@app/api/table.api';
import { Table } from '@app/components/common/Table/Table';
import { PermissionTypes, RoutesMapping, RoutesMappingTranslationKey } from '@app/constants/enums/permission';
import { notificationController } from '@app/controllers/notificationController';
import { useLoader } from '@app/hooks/useLoader';
import { useMounted } from '@app/hooks/useMounted';
import { ANY_OBJECT } from '@app/interfaces/interfaces';
import { Form, Space, TablePaginationConfig } from 'antd';
import { Button } from 'components/common/buttons/Button/Button';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableCell } from '../../editableTable/EditableCell';
import { expandedColumns } from './Column';

interface ExpandedRowProps extends React.HTMLAttributes<HTMLElement> {
  record: ANY_OBJECT;
  tableData: ANY_OBJECT;
  initialPagination: QueryTable;
  setInitialPagination: React.Dispatch<React.SetStateAction<QueryTable>>;
  fetch: (pagination: QueryTable) => void;
  permission: number;
}

interface PermissionData {
  key: string;
  title: string;
  readOnly: boolean;
  readAndWrite: boolean;
}

const ExpandedRowRender: React.FC<ExpandedRowProps> = ({
  record,
  tableData,
  initialPagination,
  setInitialPagination,
  fetch,
  permission,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editingPermissionKey, setEditingPermissionKey] = useState('');
  const { isMounted } = useMounted();
  const { loader, handleLoaderOpen, handleLoaderClose } = useLoader();
  const [permissionData, setTableData] = useState<{ data: PermissionData[]; pagination: QueryTable; loading: boolean }>(
    {
      data: [],
      pagination: initialPagination,
      loading: false,
    },
  );

  const permissionRecords = useCallback(() => {
    setTableData((permissionData) => ({ ...permissionData, loading: true }));
    const permissions = record.permissions || {};
    const pData = Object.keys(RoutesMapping).map((key) => {
      return {
        key: key,
        title: t(RoutesMappingTranslationKey[key as keyof typeof RoutesMappingTranslationKey]),
        readOnly: permissions[key] === PermissionTypes.READ || permissions[key] === PermissionTypes.READWRITE,
        readAndWrite: permissions[key] === PermissionTypes.READWRITE,
      };
    });

    if (isMounted.current) {
      setTableData({ data: pData, pagination: initialPagination, loading: false });
    }
  }, [initialPagination, isMounted, record.permissions, t]);

  useEffect(() => {
    permissionRecords();
  }, [permissionRecords]);

  const isEditingPermission = (record: ANY_OBJECT) => record.key === editingPermissionKey;

  const editPermission = (record: ANY_OBJECT) => {
    form.setFieldsValue({
      ...record,
    });

    setEditingPermissionKey(record?.key as string);
  };

  const cancelPermission = () => {
    setEditingPermissionKey('');
  };

  const savePermission = async (updatedRecord: PermissionData) => {
    handleLoaderOpen();
    try {
      const row = (await form.validateFields()) as PermissionData;
      const newData = [...permissionData.data];
      const index = newData.findIndex((item) => updatedRecord.key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setTableData({ ...permissionData, data: newData });
        setEditingPermissionKey('');
      } else {
        newData.push(row);
        setTableData({ ...permissionData, data: newData });
        setEditingPermissionKey('');
      }
      const updatePermissionValue = newData.filter((item) => item.key === updatedRecord.key)[0];
      const updatedRole = {
        role: record.role,
        permissions: {
          ...record.permissions,
          [updatePermissionValue.key]:
            updatePermissionValue.readOnly && updatePermissionValue.readAndWrite
              ? PermissionTypes.READWRITE
              : updatePermissionValue.readOnly
              ? PermissionTypes.READ
              : updatePermissionValue.readAndWrite
              ? PermissionTypes.READWRITE
              : PermissionTypes.NOTHING,
        },
        routes: record.routes,
      };
      updateRole(record.id, updatedRole)
        .then(() => {
          fetch(initialPagination);
          notificationController.success({
            message: t('roles.table.updateSuccess'),
          });
          setEditingPermissionKey('');
          fetch(initialPagination);
          handleLoaderClose();
        })
        .catch((err) => {
          handleLoaderClose();
          notificationController.error({ message: err.message });
        });
    } catch (errInfo) {}
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const newPagination = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    };
    setInitialPagination(newPagination);
  };

  const columns = [
    ...expandedColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
    {
      title: t('tables.actions'),
      dataIndex: 'actions',
      width: 'auto',
      render: (_text: string, record: PermissionData) => {
        const editable = isEditingPermission(record);
        return (
          <Space>
            {editable ? (
              <>
                <Button type="primary" loading={loader} onClick={() => savePermission(record)}>
                  {t('common.save')}
                </Button>
                <Button type="ghost" onClick={cancelPermission}>
                  {t('common.cancel')}
                </Button>
              </>
            ) : (
              <>
                <Button type="ghost" onClick={() => editPermission(record)}>
                  {t('common.edit')}
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ANY_OBJECT) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditingPermission(record),
        roleList: tableData.data,
      }),
    };
  });

  const tableColumns = () => {
    if (permission != PermissionTypes.READWRITE) {
      const tableActionToRemove = mergedColumns.findIndex((column) => column.dataIndex === 'actions');
      if (tableActionToRemove >= 0) {
        mergedColumns.splice(tableActionToRemove, 1);
      }
    }

    return mergedColumns;
  };

  return (
    <Form form={form} component={false}>
      <Table
        rowKey="id"
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        columns={tableColumns()}
        rowClassName="editable-row"
        dataSource={permissionData.data}
        pagination={false}
        onChange={handleTableChange}
        locale={{ emptyText: t('common.noData') }}
      />
    </Form>
  );
};

export default ExpandedRowRender;
